import { Languages } from '@account/enums/languages.enum';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ErrorParser } from '@app/classes/error-parser.class';
import { Columns, Row } from '@app/components/simple-table/simple-table.component';
import { FeatureFlag } from '@app/enums';
import { BadgeOptions } from '@app/interfaces/badge-options.interface';
import { Model } from '@app/models/core/base.model';
import { CompanyAdminContact } from '@app/models/payroll/company/company-admin-contact';
import { BaseForm } from '@app/modules/forms/base.form';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { CompanyAdminContactsTable } from '@app/modules/payroll/services/tables/company-admin-contacts-table/company-admin-contacts.table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { PayrollSettingsService, Settings } from '@app/services/payroll-settings.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { roeContactTableColumns } from '@payroll/meta/roe-contact-table.columns';
import { taxFormContactTableColumns } from '@payroll/meta/tax-form-contact-table.columns';
import { Subscription } from 'rxjs';

type ParsableError = Parameters<typeof ErrorParser.parse>[0];

@Component({
    styleUrls: ['./settings-show.style.scss'],
    templateUrl: './settings-show.template.html',
})
export class SettingsShowView extends BaseForm implements OnInit, OnDestroy {
    @ViewChild('adminContactSettingForm') adminContactSettingForm!: NgForm;
    isSettingsLoading = true;
    isTableLoading = true;

    companySettingsEditMode = false;
    companyTaxFormEditMode = false;

    settings?: Settings;
    originalSettings?: Settings;
    isActivateWorkersCompensationModalOpen = false;
    workersCompensationBadgeOptions?: BadgeOptions;
    canActivateWorkersCompensation = false;
    hasQcPayrollFeature = false;
    hasPayrollContactFlag = false;
    payrollEnabled = false;

    taxFormContactRows: Row[] = [];
    taxFormContactColumns: Columns[] = taxFormContactTableColumns;

    roeContactRows: Row[] = [];
    roeContactColumns: Columns[] = roeContactTableColumns;

    activeAdminContact?: CompanyAdminContact;

    currentlyOpenDialog?:
        | 'ACTIVE_WORKERS_COMPENSATION_MODAL'
        | 'TAX_FORM_CONTACT_DIALOG'
        | 'ROE_CONTACT_DIALOG'
        | 'ADMIN_CONTACT_DIALOG' = undefined;

    isEditDialogMode?: boolean = undefined;

    languages = Languages;

    private subscriptions: Subscription[] = [];

    constructor(
        private notify: NotifyService,
        public settingsService: PayrollSettingsService,
        private auth: AuthService,
        private features: FeatureService,
        public payrollModuleMenuService: PayrollModuleMenuService,
        public adminContactTable: CompanyAdminContactsTable
    ) {
        super();
    }

    get scheduleFrequency(): TranslatableKey {
        if (!this.settings?.schedule?.frequency) {
            return '';
        }

        return `payroll.settings.frequencies.${this.settings.schedule.frequency}`;
    }

    get scheduleDayOfTheWeek(): TranslatableKey {
        if (!this.settings?.schedule?.dayOfTheWeek) {
            return 'payroll.settings.na';
        }

        return `payroll.settings.daysOfTheWeek.${this.settings.schedule.dayOfTheWeek}`;
    }

    get days(): number[] | undefined {
        return this.settings?.schedule?.daysOfTheMonth;
    }

    get scheduleDaysOfTheMonth(): TranslatableKey {
        if (!this.days) {
            return 'payroll.settings.na';
        }
        if (this.days.length === 1) {
            return this.days[0].toString();
        }
        return 'payroll.settings.scheduleDaysOfTheMonth';
    }

    onTaxFormContactEdit(isEditDialogMode: boolean): void {
        this.isEditDialogMode = isEditDialogMode;
        this.currentlyOpenDialog = 'TAX_FORM_CONTACT_DIALOG';
    }

    onRoeContactEdit(isEditDialogMode: boolean): void {
        this.isEditDialogMode = isEditDialogMode;
        this.currentlyOpenDialog = 'ROE_CONTACT_DIALOG';
    }

    onAdminContactAction(isEditDialogMode: boolean, contact: CompanyAdminContact | null = null): void {
        this.isEditDialogMode = isEditDialogMode;
        this.currentlyOpenDialog = 'ADMIN_CONTACT_DIALOG';
        this.activeAdminContact = contact ?? new CompanyAdminContact();
    }

    async ngOnInit(): Promise<void> {
        this.hasQcPayrollFeature = await this.features.has(FeatureFlag.qcPayroll);
        this.hasPayrollContactFlag = await this.features.has(FeatureFlag.payrollContact);

        this.subscriptions.push(
            this.settingsService.loading$.subscribe((isLoading) => {
                this.isSettingsLoading = isLoading;
            }),

            this.settingsService.settings$.subscribe((settings) => {
                if (!this.originalSettings) {
                    this.originalSettings = { ...settings };
                }
                this.settings = settings;
                if (
                    this.settings.roeContactFirstName &&
                    this.settings.roeContactLastName &&
                    this.settings.roeContactAreaCode &&
                    this.settings.roeContactPhoneNumber
                ) {
                    this.roeContactRows = [
                        {
                            cells: [
                                this.settings.roeContactFirstName + ' ' + this.settings.roeContactLastName,
                                '(' + this.settings.roeContactAreaCode + ') ' + this.settings.roeContactPhoneNumber,
                                this.settings.roeContactPhoneExtensionNumber ?? '',
                            ],
                        },
                    ];
                }

                if (
                    this.settings.taxFormContactFirstName &&
                    this.settings.taxFormContactLastName &&
                    this.settings.taxFormContactEmail &&
                    this.settings.taxFormContactAreaCode &&
                    this.settings.taxFormContactPhoneNumber
                ) {
                    this.taxFormContactRows = [
                        {
                            cells: [
                                this.settings.taxFormContactFirstName + ' ' + this.settings.taxFormContactLastName,
                                this.settings.taxFormContactEmail,
                                '(' +
                                    this.settings.taxFormContactAreaCode +
                                    ') ' +
                                    this.settings.taxFormContactPhoneNumber,
                                this.settings.taxFormContactPhoneExtensionNumber ?? '',
                            ],
                        },
                    ];
                }
            }),

            this.adminContactTable.onLoading.subscribe((tableLoading) => {
                this.isTableLoading = tableLoading;
            })
        );

        try {
            if (!this.settings) {
                await this.settingsService.load();
            }
        } catch (e) {
            this.notify.error(e);
            this.isSettingsLoading = false;
        }

        this.payrollEnabled = this.auth.company.isPayrollSyncEnabled;
        this.canActivateWorkersCompensation = this.checkIfWorkersCompActivated();
        this.workersCompensationBadgeOptions = this.getWorkersCompensationBadgeOptions();
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    onCancel(): void {}

    onDelete(): void {}

    submitAndValidate(): boolean {
        if (this.form) {
            this.submit();
            return this.valid;
        }
        return true;
    }

    toggleEditCompanySettingsInformation(): void {
        this.companySettingsEditMode = !this.companySettingsEditMode;
    }

    toggleEditTaxFormContactInformation(): void {
        this.companyTaxFormEditMode = !this.companyTaxFormEditMode;
    }

    cancelTaxFormSettingsEdit(): void {
        this.toggleEditTaxFormContactInformation();
        this.settings = { ...this.originalSettings };
        Object.values(this.settings).forEach((value: any) => value instanceof Model && value.revert());
    }

    cancelCompanySettingsEdit(): void {
        this.toggleEditCompanySettingsInformation();
        this.settings = { ...this.originalSettings };
        Object.values(this.settings).forEach((value: any) => value instanceof Model && value.revert());
    }

    async onSave(): Promise<void> {
        this.submit();

        if (!this.valid) {
            return;
        }

        this.isSettingsLoading = true;

        try {
            await this.settingsService.save();
            await this.settingsService.reload();

            if (
                this.currentlyOpenDialog === 'TAX_FORM_CONTACT_DIALOG' ||
                this.currentlyOpenDialog === 'ROE_CONTACT_DIALOG'
            ) {
                this.closeDialog();
            }

            this.notify.success('saved');
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as ParsableError));
            this.isSettingsLoading = false;
        } finally {
            this.isSettingsLoading = false;
        }
        if (this.companySettingsEditMode) {
            this.toggleEditCompanySettingsInformation();
        }
        if (this.companyTaxFormEditMode) {
            this.toggleEditTaxFormContactInformation();
        }
    }

    getWorkersCompensationBadgeOptions(): BadgeOptions {
        if (this.canActivateWorkersCompensation) {
            return {
                label: 'payroll.settings.wcbPillNotActive',
                type: 'default',
            };
        }

        return {
            label: 'payroll.settings.wcbPillActive',
            type: 'success',
        };
    }

    openActivateWorkersCompensationModal(): void {
        this.currentlyOpenDialog = 'ACTIVE_WORKERS_COMPENSATION_MODAL';
    }

    closeDialog(revertContact: boolean = false): void {
        this.currentlyOpenDialog = undefined;
        if (revertContact) {
            this.activeAdminContact?.revert();
        }
    }

    checkIfWorkersCompActivated(): boolean {
        return this.settings?.wcbActivated === null || !this.settings?.wcbActivated;
    }

    async onActivateWorkersCompensation(): Promise<void> {
        if (!this.settings) return;

        this.isSettingsLoading = true;

        try {
            this.settings.wcbActivated = true;
            await this.settingsService.save();
            await this.settingsService.reload();
            this.canActivateWorkersCompensation = this.checkIfWorkersCompActivated();
            this.workersCompensationBadgeOptions = this.getWorkersCompensationBadgeOptions();
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as ParsableError));
            this.isSettingsLoading = false;
        }

        this.closeDialog();
    }

    async onSaveAdminContact(): Promise<void> {
        try {
            await this.activeAdminContact?.param('company', this.auth.company.id).save();

            if (this.currentlyOpenDialog === 'ADMIN_CONTACT_DIALOG') {
                this.closeDialog();
            }

            this.notify.success('payroll.settings.adminContactSaved');
            this.adminContactSettingForm.resetForm();
            this.adminContactTable.reload();
        } catch (err) {
            this.notify.error(err.message || 'payroll.settings.adminContactSaveFailed');
            this.isTableLoading = false;
        }
    }

    deleteAdminContact(contact: CompanyAdminContact): void {
        this.isTableLoading = true;
        contact
            .param('company', this.auth.company.id)
            .delete()
            .then(() => {
                this.adminContactTable.reload();
                this.notify.success('payroll.settings.adminContactDeleted');
                this.isTableLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'payroll.settings.adminContactDeleteFailed');
                this.isTableLoading = false;
            });
    }

    adminContactTableMenuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const contact = data as CompanyAdminContact;
        switch (menuItem.event) {
            case 'delete':
                this.deleteAdminContact(contact);
                break;
            case 'edit':
                this.onAdminContactAction(true, contact);
                break;
        }
    }

    maxAdminContacts(): boolean {
        return this.adminContactTable.paginator.total >= 5;
    }
}
